import PropTypes from 'prop-types';
import { Typography } from '@nextiva/next-ui-components';
import { RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { Button } from '@nextiva/next-ui';

import { INVOICE_STATUS } from 'constant';

import './MakePaymentContent.scss';
import { PaymentMethodList } from 'components/Payment';
import { getSubmitButtonLabel } from 'getters/paymentGetters/paymentGetters';

const blockName = 'make-payment-content';

/**
 * Main Payment Drawer Content Component for Summary page.
 * Includes Payment Amount selection, Payment Method List and Action Buttons.
 *
 * @param amountRadioConfig - Configuration for radio buttons.
 * @param buttonsConfig - Configuration for action buttons.
 * @param paymentListConfig - Configuration to display payment method list.
 * @param showSpinner - Flag to show or hide spinner.
 */
const MakePaymentContent = ({
  amountRadioConfig,
  buttonsConfig,
  paymentListConfig,
  showSpinner,
}) => {
  const { amount, payment } = amountRadioConfig;

  return (
    <div
      className={blockName}
      data-testid="BillingLicensing-MakePaymentContent"
      style={{ display: showSpinner ? 'none' : 'block' }}
    >
      <div className={`${blockName}__payment`}>
        <Typography className={`${blockName}__payment__title`} component="h3">
          Payment Amount
        </Typography>
        <p>AMOUNT</p>
        <RadioGroup
          id="payment"
          aria-label="payment"
          name="payment"
          value={payment}
          onChange={amountRadioConfig.onChangePayment}
        >
          <FormControlLabel
            value="subscription"
            control={<Radio />}
            label={
              <span>
                Total Amount: <strong>${amount.totalMonthlyBill}</strong>
              </span>
            }
            disabled={INVOICE_STATUS.CLOSED === amountRadioConfig.invoiceStatus}
          />
          <FormControlLabel
            value="usage"
            control={<Radio />}
            label={
              <span>
                Usage Overage: <strong>${amount.usageOverage}</strong>
              </span>
            }
            disabled={!amountRadioConfig.hasOverage}
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            disabled={!amountRadioConfig.hasOverage}
            label={
              <div className={`${blockName}__payment__other`}>
                Other{' '}
                <input
                  aria-label="Other amount"
                  disabled={payment !== 'other'}
                  id={`${blockName}__payment__other`}
                  max={amount.usageOverage}
                  min="0.01"
                  onChange={amountRadioConfig.handleOtherChange}
                  step="0.01"
                  type="number"
                  value={amountRadioConfig.other}
                />
              </div>
            }
          />
        </RadioGroup>
      </div>
      <PaymentMethodList
        startDeleteCreditCard={paymentListConfig.startDeleteCreditCard}
        startSetDefaultPayment={paymentListConfig.setDefaultPayment}
        corpAcctNbr={paymentListConfig.corpAcctNbr}
        paymentMethods={paymentListConfig.paymentMethods}
        isSummaryPage={paymentListConfig.isSummaryPage}
        billToSiteNumber={paymentListConfig.billToSiteNumber}
        addAlertDetails={paymentListConfig.addAlertDetails}
      />
      <div className={`${blockName}__add-card`} onClick={buttonsConfig.addCard}>
        <span>+ Add New</span>
      </div>
      <Button
        className={`${blockName}__drawer-btn ${blockName}--right-margin`}
        kind="default"
        onClick={buttonsConfig.onClose}
      >
        Cancel
      </Button>
      <Button
        className={`${blockName}__drawer-btn ${blockName}--big-button`}
        disabled={buttonsConfig.submitButtonDisabled}
        kind="primary"
        onClick={buttonsConfig.processBill}
      >
        {getSubmitButtonLabel(amountRadioConfig)}
      </Button>
    </div>
  );
};

MakePaymentContent.propTypes = {
  amountRadioConfig: PropTypes.shape({
    amount: PropTypes.shape({
      totalMonthlyBill: PropTypes.number.isRequired,
      usageOverage: PropTypes.number.isRequired,
    }).isRequired,
    handleOtherChange: PropTypes.func.isRequired,
    hasOverage: PropTypes.bool.isRequired,
    invoiceStatus: PropTypes.string.isRequired,
    onChangePayment: PropTypes.func.isRequired,
    other: PropTypes.number.isRequired,
    payment: PropTypes.string.isRequired,
  }).isRequired,
  buttonsConfig: PropTypes.shape({
    addCard: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    processBill: PropTypes.func.isRequired,
    submitButtonDisabled: PropTypes.bool.isRequired,
  }).isRequired,
  paymentListConfig: PropTypes.shape({
    addAlertDetails: PropTypes.func.isRequired,
    billToSiteNumber: PropTypes.string.isRequired,
    corpAcctNbr: PropTypes.number.isRequired,
    isSummaryPage: PropTypes.bool.isRequired,
    paymentMethods: PropTypes.array.isRequired,
    setDefaultPayment: PropTypes.func.isRequired,
    startDeleteCreditCard: PropTypes.func.isRequired,
  }).isRequired,
  showSpinner: PropTypes.bool.isRequired,
};

export default MakePaymentContent;
